import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Select — a choice from a short list, in a popup the app styles (ADR-0088
 * D2). Admin's filters and forms use it; a customer form uses `NativeSelect`,
 * whose phone picker is the one people already know.
 *
 * Built on Base UI (ADR-0088 D3), replacing Admin's Radix select. The wrapper
 * keeps the API Admin's call sites were written against, so none of them
 * changed:
 *
 * - **The trigger shows the chosen option's label.** Base UI's `Select.Value`
 *   shows the raw value unless the root is given the items, which Radix did
 *   not need. `Select` reads every `SelectItem` among its children (through
 *   fragments, groups and the content) and passes their labels on. A
 *   `SelectItem` rendered inside another component is not found, and its
 *   value then shows raw: keep the items in the `Select`'s own JSX.
 * - **`""` is a real option.** Optional filters use it for "All" or "Any".
 *   Base UI treats it as "no selection" but still selects it and shows its
 *   label, so no sentinel is needed; with a `placeholder`, the placeholder
 *   shows instead. A `required` select left at `""` fails form validation.
 * - **`onValueChange` receives a string**, `""` when Base UI reports `null`.
 * - **Only a person changes the value.** Base UI resets the value when the
 *   option list changes and no longer holds it, but it compares the list
 *   with the value from before the render. So choosing a replacement for an
 *   option that disappears once replaced (Admin's retired Program, an
 *   "Unavailable selection") was undone at once. The wrapper cancels those
 *   resets: a change Base UI makes on its own, without a native event and
 *   not while someone types on the closed trigger.
 * - The popup opens below the trigger, at least as wide as it, as Radix's
 *   `popper` mode did, instead of Base UI's default of covering the trigger.
 *
 * `selectTriggerVariants` styles any select-shaped trigger, such as Admin's
 * pickers, which open a popover with a search box instead.
 */
import { Select as SelectPrimitive } from '@base-ui/react/select';
import { cva } from 'class-variance-authority';
import { Icon } from './icon.js';
import { Children, createContext, forwardRef, isValidElement, useContext, useMemo, useRef, } from 'react';
import { cn } from './cn.js';
import { fieldBase, fieldCompounds, fieldHeight, fieldSize, fieldSurface } from './field-shape.js';
/** Collects `value → label` from every `SelectItem` among `node`'s descendants. */
function collectItemLabels(node, labels) {
    Children.forEach(node, (child) => {
        if (!isValidElement(child))
            return;
        const { value, children } = child.props;
        if (child.type === SelectItem && typeof value === 'string')
            labels[value] = children;
        else
            collectItemLabels(children, labels);
    });
}
const SelectContext = createContext(null);
export function Select({ children, onValueChange, onOpenChange, ...props }) {
    const items = useMemo(() => {
        const labels = {};
        collectItemLabels(children, labels);
        return labels;
    }, [children]);
    const typing = useRef(false);
    const context = useMemo(() => ({ typing, emptyIsOption: '' in items }), [items]);
    return (_jsx(SelectPrimitive.Root, { items: items, onValueChange: (next, details) => {
            // A press or autofill carries a native event; typeahead happens while
            // a key is down. Anything else is Base UI's own list-change reset.
            if (details.reason === 'none' && details.event.type === 'base-ui' && !typing.current) {
                details.cancel();
                return;
            }
            onValueChange?.(next ?? '');
        }, onOpenChange: (open) => onOpenChange?.(open), ...props, children: _jsx(SelectContext.Provider, { value: context, children: children }) }));
}
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;
const triggerVariants = cva(`flex items-center justify-between gap-2 text-left ${fieldBase} [&>span]:truncate`, {
    variants: { surface: fieldSurface, size: fieldSize },
    compoundVariants: [...fieldCompounds],
    defaultVariants: { size: 'default', surface: 'inset' },
});
/**
 * The complete trigger classes for a size and surface: shape, height and
 * fill. Also styles a select-shaped trigger that opens something else, such
 * as Admin's searchable pickers and Manager's month and cohort pickers.
 */
export function selectTriggerVariants({ surface, size = 'default' } = {}) {
    return cn(triggerVariants({ size, surface }), fieldHeight[size ?? 'default']);
}
export const SelectTrigger = forwardRef(({ className, children, surface, size, ...props }, ref) => {
    const select = useContext(SelectContext);
    return (_jsxs(SelectPrimitive.Trigger, { ref: ref, "data-slot": "select-trigger", className: cn(selectTriggerVariants({ surface, size }), 
        // An unchosen value reads as an input's placeholder of the same size.
        // Base UI marks an empty value as a placeholder, but a `""` option
        // ("All", "Everyone") is a real choice and keeps the text color.
        !select?.emptyIsOption &&
            (size === 'lg' ? 'data-placeholder:text-text-faint' : 'data-placeholder:text-text-secondary'), className), onKeyDownCapture: () => {
            if (select === null)
                return;
            select.typing.current = true;
            // Typeahead chooses during this keydown; clear once it is handled.
            queueMicrotask(() => {
                select.typing.current = false;
            });
        }, ...props, children: [children, _jsx(SelectPrimitive.Icon, { "data-slot": "select-icon", className: "flex shrink-0 opacity-60", children: _jsx(Icon, { name: "expand" }) })] }));
});
SelectTrigger.displayName = 'SelectTrigger';
export const SelectContent = forwardRef(({ className, children, ...props }, ref) => (_jsx(SelectPrimitive.Portal, { children: _jsx(SelectPrimitive.Positioner, { className: "z-50", alignItemWithTrigger: false, sideOffset: 4, children: _jsx(SelectPrimitive.Popup, { ref: ref, className: cn('relative flex max-h-[min(24rem,var(--available-height))] min-w-[max(8rem,var(--anchor-width))] flex-col ' +
                'overflow-hidden rounded-token-card border border-border-default bg-surface-card text-text-primary shadow-menu outline-none', className), ...props, children: _jsx(SelectPrimitive.List, { className: "overflow-y-auto p-1", children: children }) }) }) })));
SelectContent.displayName = 'SelectContent';
/** The heading of a `SelectGroup`. */
export const SelectLabel = forwardRef(({ className, ...props }, ref) => (_jsx(SelectPrimitive.GroupLabel, { ref: ref, className: cn('px-2 py-1.5 text-label font-medium text-text-secondary', className), ...props })));
SelectLabel.displayName = 'SelectLabel';
export const SelectItem = forwardRef(({ className, children, ...props }, ref) => (_jsxs(SelectPrimitive.Item, { ref: ref, className: cn('relative flex w-full cursor-default select-none items-center rounded-token-input py-1.5 pl-8 pointer-coarse:min-h-11 pr-2 text-body outline-none ' +
        'data-highlighted:bg-surface-app data-disabled:pointer-events-none data-disabled:opacity-50', className), ...props, children: [_jsx("span", { className: "absolute left-2 flex size-4 items-center justify-center", children: _jsx(SelectPrimitive.ItemIndicator, { children: _jsx(Icon, { name: "selected", size: 14 }) }) }), _jsx(SelectPrimitive.ItemText, { children: children })] })));
SelectItem.displayName = 'SelectItem';
export const SelectSeparator = forwardRef(({ className, ...props }, ref) => (_jsx(SelectPrimitive.Separator, { ref: ref, className: cn('-mx-1 my-1 h-px bg-border-default', className), ...props })));
SelectSeparator.displayName = 'SelectSeparator';
//# sourceMappingURL=select.js.map